import React, { useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment, InputLabel,
  OutlinedInput,
  TextField,
  Theme,
  Typography
} from '@mui/material';

import { CrossCircleFilledIcon, PasswordVisibilityOff, PasswordVisibilityOn } from '../../Icons';

interface PasswordInputProps {
  label: string,
  id: string,
  name: string,
  initialValue: string,
  onChange?: (value: string) => void,
  validationFn: (value: string) => string,
  sx?: any,
}

export const PasswordInput = ({
  id,
  name,
                                initialValue,
                                label,
                                validationFn,
                                onChange,
                                sx,
                              }: PasswordInputProps) => {

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [value, setValue] = useState<string>(initialValue);

  const error = useMemo(() => {
    return isDirty && validationFn(value);
  }, [value, validationFn]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleChangeValue = (e: any) => {
    setValue(e.target.value);
    onChange?.(e.target.value);
  };

  return (
    <FormControl margin="normal" fullWidth variant="outlined">
      <InputLabel htmlFor={id} sx={{
        color: !value ? '#828282' : '#080808',
        textTransform: !value ? 'capitalize' : 'uppercase',
        fontWeight: '500',
        fontSize: !value ? '15px' : '16px',
        top: !value ? '0px' : '16px',

        '&.Mui-focused': {
          fontWeight: '500',
          fontSize: '16px',
          color: '#080808',
          top: '16px',
          textTransform: 'uppercase',
        }
      }}>{label}</InputLabel>
      <OutlinedInput
        id={id}
        fullWidth
        name={name}
        type={showPassword ? 'text' : 'password'}
        autoComplete="current-password"
        onFocus={() => setIsDirty(true)}
        onChange={handleChangeValue}
        defaultValue={initialValue}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              disableRipple
            >
              {showPassword ? <PasswordVisibilityOff /> : <PasswordVisibilityOn />}
            </IconButton>
          </InputAdornment>
        }
        sx={{
          flex: 1,
          color: '#080808',
          fontSize: '15px',
          '&::placeholder': {
            color: 'red',
          },

          '& .MuiOutlinedInput-input': {
            marginTop: '8.5px',
            paddingBottom: '8px',
          },
          '&.MuiOutlinedInput-root': {
            borderRadius: '6px',

            '& fieldset': {
              borderColor: error ? '#D62F39' : '#D6D6DB',
            },

            '&.Mui-focused fieldset': {
              borderColor: '#828282',
              borderWidth: '1px',
            }
          },
          ...sx,
        }}
      />
      {error ? (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          color: '#D62F39',
          marginTop: '6px',
        }}>
          <CrossCircleFilledIcon fontSize="inherit" />
          <Typography sx={{
            color: '#D62F39',
            fontSize: '14px',
            fontWeight: '500',
            marginLeft: '6px',
          }}>{error}</Typography>
        </Box>) : null}
    </FormControl>
  );
};
